import _ from "underscore";
import { MillenniumDate } from "./MillenniumDate.js";
import { MillenniumTime } from "./MillenniumTime.js";
import { MillenniumWeek } from "./MillenniumWeek.js";
import { MillenniumWeekday } from "./MillenniumWeekday.js";
import { SimpleDateFormat } from "./SimpleDateFormat.js";
const MIN_DATE_TIME = 0;
const MAX_DATE_TIME = 2145916799;
export class MillenniumDateTime {
    static ZERO = new MillenniumDateTime(0);
    static MIN_DATE_TIME = MIN_DATE_TIME;
    static MAX_DATE_TIME = MAX_DATE_TIME;
    static INVALID_DATE_TIME = null;
    static UNDEFINED_DATE_TIME = 2147483647;
    mts;
    constructor(millenniumTimeStampOrDate, millenniumTime) {
        if (millenniumTime === undefined) {
            if (millenniumTimeStampOrDate instanceof MillenniumDateTime) {
                this.mts = millenniumTimeStampOrDate.getMts();
            }
            else if (typeof millenniumTimeStampOrDate === "number") {
                if (millenniumTimeStampOrDate > -1 && millenniumTimeStampOrDate <= MAX_DATE_TIME) {
                    this.mts = millenniumTimeStampOrDate;
                }
                else if (millenniumTimeStampOrDate > MAX_DATE_TIME) {
                    throw new Error(`A MillenniumDateTime may not have a timestamp greater than ${MAX_DATE_TIME}`);
                }
                else if (millenniumTimeStampOrDate < MIN_DATE_TIME) {
                    throw new Error(`A MilleniumDateTime may not have a timestamp less than ${MIN_DATE_TIME}`);
                }
            }
            else {
                throw new Error(`A MillenniumDateTime can not be created from the data type of ${millenniumTimeStampOrDate}`);
            }
        }
        else {
            this.mts =
                millenniumTimeStampOrDate.getDayNumber() * 86400 +
                    millenniumTime.getTimeNumber();
            if (this.mts > MAX_DATE_TIME) {
                throw new Error(`A MillenniumDateTime may not have a timestamp greater than ${MAX_DATE_TIME}`);
            }
            else if (this.mts < MIN_DATE_TIME) {
                throw new Error(`A MilleniumDateTime may not have a timestamp less than ${MIN_DATE_TIME}`);
            }
        }
        if (this.mts === null) {
            throw new Error("Attempted to create MillenniumDateTime with null time");
        }
        if (isNaN(this.mts)) {
            throw new Error("Attempted to create MillenniumDateTime with NaN time");
        }
    }
    static fromYyyyMMddHHmmss(yearString, monthString, dayString, hourString, minuteString, secondString, accept2400) {
        const year = parseInt(yearString, 10) || 0;
        if (year < MillenniumDate.MIN_YEAR || year > MillenniumDate.MAX_YEAR) {
            throw new Error(`Year ${year} out of bounds.`);
        }
        const month = parseInt(monthString, 10) || 0;
        if (month < 1 || month > 12) {
            throw new Error(`Month ${month} out of bounds.`);
        }
        const day = parseInt(dayString, 10) || 0;
        if (day < 1 || day > 31) {
            throw new Error(`Day of month ${day} out of bounds.`);
        }
        const hours = parseInt(hourString, 10) || 0;
        if (hours < 0 || hours > 24) {
            throw new Error(`Hour of day ${hours} out of bounds.`);
        }
        const minutes = parseInt(minuteString, 10) || 0;
        if (minutes < 0 || minutes > 59) {
            throw new Error(`Minutes ${minutes} out of bounds.`);
        }
        const seconds = parseInt(secondString, 10) || 0;
        if (seconds < 0 || seconds > 59) {
            throw new Error(`Seconds ${seconds} out of bounds.`);
        }
        if (hours === 24) {
            if (!accept2400 || minutes !== 0 || seconds !== 0) {
                throw new Error("Hour could not be set to 24.");
            }
        }
        const dayNumber = MillenniumDate.create(year, month, day).getDayNumber();
        const mts = dayNumber * 86400 + hours * 3600 + minutes * 60 + seconds;
        if (mts >= MillenniumDateTime.MIN_DATE_TIME && mts <= MillenniumDateTime.MAX_DATE_TIME) {
            return new MillenniumDateTime(mts);
        }
        throw new Error("Millennium timestamp out of bounds.");
    }
    static fromYyyyMMddHHmmssString(dts) {
        if (dts === null || dts.length !== 14) {
            return null;
        }
        return MillenniumDateTime.fromYyyyMMddHHmmss(dts.substring(0, 4), // yyyy
        dts.substring(4, 6), // MM
        dts.substring(6, 8), // dd
        dts.substring(8, 10), // HH
        dts.substring(10, 12), // mm
        dts.substring(12, 14) // ss
        );
    }
    static fromYyyyMMdd(dateString) {
        return MillenniumDateTime.fromDate(MillenniumDate.fromYyyyMMdd(dateString));
    }
    static fromIcal(ical, accept2400) {
        const length = ical !== null ? ical.length : 0;
        if (ical === null || length < 15 || length > 16) {
            return null;
        }
        ical = ical.toLowerCase();
        if (ical.charAt(8) !== "t") {
            return null;
        }
        if (ical.length === 16 && ical.charAt(15) !== "z") {
            return null;
        }
        return MillenniumDateTime.fromYyyyMMddHHmmss(ical.substring(0, 4), // yyyy
        ical.substring(4, 6), // MM
        ical.substring(6, 8), // dd
        ical.substring(9, 11), // HH
        ical.substring(11, 13), // mm
        ical.substring(13, 15), // ss
        accept2400);
    }
    static fromYearMonthDay(year, month, day) {
        return MillenniumDateTime.fromDate(MillenniumDate.create(year, month, day));
    }
    /**
     * @param date
     * @return A new MillenniumDateTime set to the start of the given date.
     */
    static fromDate(date) {
        if (date === null) {
            return null;
        }
        return new MillenniumDateTime(date.getDayNumber() * 86400);
    }
    static fromDayNumber(dayNumber) {
        return new MillenniumDateTime(dayNumber * 86400);
    }
    static createFromList(list
    // TODO: This methods returns too many type variants. We need to fix this.
    ) {
        const isInstanceOf = (obj, Type) => obj instanceof Type || (Array.isArray(obj) && obj[0] instanceof Type);
        const asArray = (val) => (Array.isArray(val) ? val : [val]);
        const categories = {};
        list.forEach((item) => {
            if (isInstanceOf(item, MillenniumTime)) {
                categories.time = item;
            }
            else if (isInstanceOf(item, MillenniumDate)) {
                categories.date = item;
            }
            else if (isInstanceOf(item, MillenniumWeek)) {
                categories.week = item;
            }
            else if (isInstanceOf(item, MillenniumWeekday)) {
                categories.weekday = item;
            }
        });
        let dates = null;
        if (categories.date) {
            dates = asArray(categories.date).map((item) => MillenniumDateTime.fromDate(item));
        }
        else if (categories.week && categories.weekday) {
            dates = _.flatten(asArray(categories.week).map((week) => {
                const startOfWeek = week.getStartOfWeek();
                return asArray(categories.weekday).map((weekday) => {
                    return startOfWeek.addDays(weekday.daysFromWeekday(week.getFirstDayOfWeek()));
                });
            }));
        }
        else {
            throw new Error("Could not create a date from the given list.");
        }
        if (categories.time) {
            const categoriesTimeArr = asArray(categories.time);
            dates = _.flatten(dates?.map((item) => {
                return categoriesTimeArr.map((time) => item?.addSeconds(time.getTimeNumber()));
            }));
        }
        if (dates?.length === 1) {
            return dates[0];
        }
        dates?.sort((a, b) => a.getMts() - b.getMts());
        return dates;
    }
    static lengthInDays(begin, end) {
        if (end.isAfter(begin)) {
            return (end.getMillenniumDate().getDayNumber() - begin.getMillenniumDate().getDayNumber());
        }
        return begin.getMillenniumDate().getDayNumber() - end.getMillenniumDate().getDayNumber();
    }
    /* Instance functions */
    getMts() {
        return this.mts;
    }
    addYears(years) {
        const date = this.getMillenniumDate();
        let year = date.getYear();
        year = year + years;
        let month = date.getMonth(); // Returns 0 to 11.
        month++;
        const day = date.getDate();
        return new MillenniumDateTime(MillenniumDate.create(year, month, day), this.getMillenniumTime());
    }
    addMonths(months) {
        const date = this.getMillenniumDate();
        let year = date.getYear();
        let month = date.getMonth(); // Returns 0 to 11.
        month++;
        const startMonth = month;
        month = month + months;
        if (month > 12) {
            year = year + Math.floor(month / 12);
            month = month % 12;
        }
        else if (month === 0) {
            year--;
            month = 12;
        }
        else if (month < 0) {
            if (Math.floor(month / 12) === 0) {
                year--;
            }
            else {
                year = year + Math.floor(month / 12);
            }
            let monthsLeft = month % 12;
            if (-monthsLeft > startMonth) {
                year--;
                monthsLeft = 12 + monthsLeft;
            }
            else {
                monthsLeft = 12 + monthsLeft;
            }
            month = monthsLeft;
        }
        const day = date.getDate();
        if (month === 0) {
            month = 12;
            year--;
        }
        return new MillenniumDateTime(MillenniumDate.create(year, month, day), this.getMillenniumTime());
    }
    /**
     * @return A new MillenniumDateTime days number of days after the invoked MillenniumDateTime.
     */
    addDays(days) {
        return new MillenniumDateTime(this.mts + days * 86400);
    }
    /**
     * @return A new MillenniumDateTime weeks number of weeks after the invoked MillenniumDateTime.
     */
    addWeeks(weeks) {
        return this.addDays(weeks * 7);
    }
    /**
     * @return A new MillenniumDateTime seconds number of seconds after the invoked MillenniumDateTime.
     */
    addSeconds(seconds) {
        return new MillenniumDateTime(this.mts + seconds);
    }
    /**
     * @return A MillenniumDate object
     */
    getMillenniumDate() {
        return new MillenniumDate(Math.floor(this.mts / 86400));
    }
    /**
     * @return A MillenniumTime object
     */
    getMillenniumTime() {
        return new MillenniumTime(this.mts % 86400);
    }
    isMidnight() {
        return this.mts % 86400 === 0;
    }
    isBefore(otherDateTime) {
        return this.mts < otherDateTime.mts;
    }
    isAfter(otherDateTime) {
        return this.mts > otherDateTime.mts;
    }
    /** 0 - 23 */
    getHours() {
        return Math.floor((this.mts % 86400) / 3600);
    }
    getMinutes() {
        return Math.floor(((this.mts % 86400) % 3600) / 60);
    }
    getSeconds() {
        return Math.floor(((this.mts % 86400) % 3600) % 60);
    }
    /**
     * @return A new MillenniumDateTime at the start of the day of the invoked MillenniumDateTime.
     */
    getStartOfDay() {
        return new MillenniumDateTime(this.mts - (this.mts % 86400));
    }
    /**
     * @return A new MillenniumDateTime at the end of the day of the invoked MillenniumDateTime.
     */
    getEndOfDay() {
        return new MillenniumDateTime(this.mts - (this.mts % 86400) + 86400);
    }
    daysBetween(other) {
        return this.getMillenniumDate().daysBetween(other.getMillenniumDate());
    }
    getYear() {
        return this.getMillenniumDate().getYear();
    }
    /**
     * Returns 0 to 11.
     */
    getMonth() {
        return this.getMillenniumDate().getMonth();
    }
    /**
     * Return date in month (1-31).
     * @return {int} date in month
     */
    getDate() {
        return this.getMillenniumDate().getDate();
    }
    /**
     * Return the day of week as an index between 0 and 6.
     * @return {int} day of week
     */
    getDay() {
        return this.getMillenniumDate().getDay();
    }
    /**
     * Return the week number of the date. The default return value includes the year,
     * for example so that week 50 of 2012 returns 1250. Setting the includeYear parameter
     * to false would return only the week, i.e. 50.
     * @param  {boolean} includeYear should the return value include the year
     * @return {int}             week for date
     */
    getWeek(includeYear, firstDayOfWeek, daysInFirstWeek) {
        return this.getMillenniumDate().getWeek(includeYear, firstDayOfWeek, daysInFirstWeek);
    }
    getWeekYear(firstDayOfWeek, daysInFirstWeek) {
        return this.getMillenniumDate().getWeekYear(firstDayOfWeek, daysInFirstWeek);
    }
    equals(obj) {
        if (this === obj) {
            return true;
        }
        if (obj === null || obj === undefined) {
            return false;
        }
        if (!(obj instanceof MillenniumDateTime)) {
            return false;
        }
        return this.mts === obj.mts;
    }
    format(format) {
        format = format || "yyyyMMdd HH:mm:ss";
        return SimpleDateFormat.format(this, format);
    }
    toIcal(isUtc) {
        const result = this.format("yyyyMMddTHHmmss");
        if (isUtc) {
            return `${result}Z`;
        }
        return result;
    }
    toString() {
        return this.format();
    }
}
